import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Play, Crown, Sparkles, CreditCard, ExternalLink, Download, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { Loader } from "@/components/common/Loader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { useLiveCoach, LEVELS } from "@/context/LiveCoachContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const QUALITY = ["720p30", "1080p30", "1080p60", "1440p60", "4K30"];
const SENS = ["low", "balanced", "high"];
const BUFFERS = [30, 60, 90, 120];
const LIMITS = [10, 25, 50, 100, 250];
const FORMATS = ["pdf", "html", "json"];
const NOTIFS = [["new_clip", "New Clip"], ["new_report", "New Report"], ["weekly_progress", "Weekly Progress"], ["coaching_complete", "Coaching Complete"], ["achievement", "Achievement Unlocked"]];

function Row({ label, hint, children }) {
    return (
        <div className="flex items-center justify-between gap-4 py-3.5 border-b border-border/60 last:border-0">
            <div><div className="text-sm text-white">{label}</div>{hint && <div className="text-xs text-muted-foreground">{hint}</div>}</div>
            {children}
        </div>
    );
}

function BillingCard() {
    const { user } = useAuth();
    const ent = user?.entitlements || {};
    const isOwner = !!user?.is_owner;
    const plan = ent.plan || "free";
    const status = ent.subscription_status;
    const [busy, setBusy] = useState(false);

    const PLAN_NAME = { free: "Free", pro: "WhiteWater Pro", creator: "WhiteWater Creator", owner: "Owner (All Access)" };
    const portal = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/billing/portal", { origin: window.location.origin });
            window.location.href = data.url;
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not open billing portal.");
            setBusy(false);
        }
    };

    return (
        <div className="bg-surface border border-border rounded-sm p-6 mb-6" data-testid="billing-card">
            <h3 className="font-heading font-bold mb-4 flex items-center gap-2"><CreditCard className="w-4 h-4 text-volt" /> Subscription</h3>
            <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-sm bg-volt/15 border border-volt/40 grid place-items-center">
                        {isOwner || plan === "creator" ? <Crown className="w-5 h-5 text-volt" /> : plan === "pro" ? <Sparkles className="w-5 h-5 text-volt" /> : <CreditCard className="w-5 h-5 text-muted-foreground" />}
                    </div>
                    <div>
                        <div className="text-white font-semibold" data-testid="current-plan">{PLAN_NAME[plan]}
                            {ent.plan_interval && <span className="text-xs text-muted-foreground font-mono ml-1">/ {ent.plan_interval}</span>}</div>
                        <div className="text-xs text-muted-foreground capitalize">{isOwner ? "Comped — all features unlocked, forever" : status ? status.replace("_", " ") : (plan === "free" ? "No active subscription" : "Active")}</div>
                    </div>
                </div>
                {isOwner ? null : plan === "free" ? (
                    <Link to="/pricing" data-testid="settings-upgrade" className="h-10 px-5 rounded-sm bg-volt text-black font-semibold hover:bg-[#33ceff] flex items-center gap-2">
                        <Sparkles className="w-4 h-4" /> Upgrade
                    </Link>
                ) : (
                    <button onClick={portal} disabled={busy} data-testid="manage-billing"
                        className="h-10 px-5 rounded-sm border border-volt/50 text-volt hover:bg-volt/10 font-semibold flex items-center gap-2 disabled:opacity-50">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />} Manage Billing
                    </button>
                )}
            </div>
        </div>
    );
}

export default function Settings() {
    const [s, setS] = useState(null);
    const [saving, setSaving] = useState(false);
    const coach = useLiveCoach();
    const { logout } = useAuth();
    const [accountBusy, setAccountBusy] = useState(false);
    const [deletePassword, setDeletePassword] = useState("");
    const [deleteConfirmation, setDeleteConfirmation] = useState("");

    useEffect(() => { api.get("/settings").then(({ data }) => setS(data)); }, []);
    if (!s) return <Loader label="Loading settings" />;

    const set = (k, v) => setS({ ...s, [k]: v });
    const setNotif = (k, v) => setS({ ...s, notifications: { ...s.notifications, [k]: v } });

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/settings", {
                recording_quality: s.recording_quality, buffer_seconds: s.buffer_seconds,
                storage_limit_gb: s.storage_limit_gb, ai_sensitivity: s.ai_sensitivity,
                export_format: s.export_format, monitor: s.monitor, audio_capture: s.audio_capture,
                notifications: s.notifications,
            });
            toast.success("Settings saved.");
        } catch { toast.error("Save failed."); }
        finally { setSaving(false); }
    };

    const exportAccount = async () => {
        setAccountBusy(true);
        try {
            const { data } = await api.get("/account/export", { responseType: "blob" });
            const url = URL.createObjectURL(data);
            const a = document.createElement("a");
            a.href = url;
            a.download = "whitewater-account-export.json";
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Account export downloaded.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Account export failed.");
        } finally { setAccountBusy(false); }
    };

    const deleteAccount = async () => {
        if (deleteConfirmation !== "DELETE") {
            toast.error("Type DELETE to confirm permanent account deletion.");
            return;
        }
        setAccountBusy(true);
        try {
            await api.delete("/account", { data: { password: deletePassword, confirmation: deleteConfirmation } });
            await logout();
            window.location.assign("/");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Account deletion failed.");
            setAccountBusy(false);
        }
    };

    const Sel = ({ k, opts, fmt }) => (
        <Select value={String(s[k])} onValueChange={(v) => set(k, isNaN(v) ? v : opts === BUFFERS || opts === LIMITS ? Number(v) : v)}>
            <SelectTrigger className="w-[150px] h-9 bg-obsidian border-border rounded-sm" data-testid={`setting-${k}`}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-surface2 border-border">
                {opts.map((o) => <SelectItem key={o} value={String(o)}>{fmt ? fmt(o) : o}</SelectItem>)}
            </SelectContent>
        </Select>
    );

    return (
        <div className="ww-fade-up max-w-3xl">
            <PageHeader title="Settings" subtitle="Tune recording, storage, AI and notifications.">
                <button data-testid="save-settings" onClick={save} disabled={saving}
                    className="h-10 px-5 rounded-sm bg-volt text-black font-semibold hover:bg-[#33ceff] flex items-center gap-2 disabled:opacity-60">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
                </button>
            </PageHeader>

            <BillingCard />

            <div className="bg-surface border border-border rounded-sm p-6 mb-6">
                <h3 className="font-heading font-bold mb-2">Recording & AI</h3>
                <Row label="Recording Quality"><Sel k="recording_quality" opts={QUALITY} /></Row>
                <Row label="Rolling Buffer" hint="Seconds before/after an event"><Sel k="buffer_seconds" opts={BUFFERS} fmt={(o) => `${o}s`} /></Row>
                <Row label="Storage Limit"><Sel k="storage_limit_gb" opts={LIMITS} fmt={(o) => `${o} GB`} /></Row>
                <Row label="AI Sensitivity" hint="Event detection threshold"><Sel k="ai_sensitivity" opts={SENS} fmt={(o) => o[0].toUpperCase() + o.slice(1)} /></Row>
                <Row label="Export Format"><Sel k="export_format" opts={FORMATS} fmt={(o) => o.toUpperCase()} /></Row>
                <Row label="Monitor Selection">
                    <input value={s.monitor || ""} onChange={(e) => set("monitor", e.target.value)} data-testid="setting-monitor"
                        className="w-[200px] h-9 bg-obsidian border border-border rounded-sm px-3 text-sm text-white" />
                </Row>
                <Row label="Audio Capture" hint="Capture game + mic audio">
                    <Switch checked={s.audio_capture} onCheckedChange={(v) => set("audio_capture", v)} data-testid="setting-audio" className="data-[state=checked]:bg-volt" />
                </Row>
            </div>

            <div className="bg-surface border border-border rounded-sm p-6 mb-6">
                <h3 className="font-heading font-bold mb-2">Live AI Coach</h3>
                <Row label="Live Verbal Coaching" hint="Real-time callouts while you play">
                    <Switch checked={coach.enabled} onCheckedChange={coach.setEnabled} data-testid="setting-live-coach" className="data-[state=checked]:bg-volt" />
                </Row>
                <Row label="Coaching Level">
                    <Select value={coach.level} onValueChange={coach.setLevel}>
                        <SelectTrigger className="w-[150px] h-9 bg-obsidian border-border rounded-sm" data-testid="setting-live-level"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-surface2 border-border">
                            {Object.entries(LEVELS).map(([k, c]) => <SelectItem key={k} value={k}>{c.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </Row>
                <Row label="Voice Callouts" hint="Speak cues aloud (text-to-speech)">
                    <Switch checked={coach.voice} onCheckedChange={coach.setVoice} data-testid="setting-live-voice" className="data-[state=checked]:bg-volt" />
                </Row>
                {coach.voice && (
                    <Row label="Coach Voice" hint="Browser text-to-speech voice">
                        <div className="flex gap-2 items-center">
                            <Select value={coach.voiceUri || "__default__"} onValueChange={(v) => coach.setVoiceUri(v === "__default__" ? "" : v)}>
                                <SelectTrigger className="w-[160px] h-9 bg-obsidian border-border rounded-sm" data-testid="setting-coach-voice"><SelectValue placeholder="System default" /></SelectTrigger>
                                <SelectContent className="bg-surface2 border-border max-h-60">
                                    <SelectItem value="__default__">System Default</SelectItem>
                                    {coach.voices.map((v) => <SelectItem key={v.voiceURI} value={v.voiceURI}>{v.name} ({v.lang})</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <button type="button" onClick={coach.testVoice} data-testid="setting-test-voice" title="Test voice"
                                className="h-9 px-3 rounded-sm border border-volt/50 text-volt hover:bg-volt/10 flex items-center gap-1.5 text-sm font-semibold shrink-0">
                                <Play className="w-3.5 h-3.5" /> Test
                            </button>
                        </div>
                    </Row>
                )}
                <Row label="Coach Volume" hint={`${Math.round((coach.volume ?? 1) * 100)}% loudness`}>
                    <div className="w-[160px]">
                        <Slider value={[coach.volume ?? 1]} onValueChange={([v]) => coach.setVolume(v)} min={0} max={1} step={0.05}
                            className="[&_[role=slider]]:border-volt/60 [&>span>span]:bg-volt" data-testid="setting-coach-volume" />
                    </div>
                </Row>
            </div>

            <div className="bg-surface border border-border rounded-sm p-6 mb-6">
                <h3 className="font-heading font-bold mb-2">Notifications</h3>
                {NOTIFS.map(([k, label]) => (
                    <Row key={k} label={label}>
                        <Switch checked={s.notifications?.[k]} onCheckedChange={(v) => setNotif(k, v)} data-testid={`notif-${k}`} className="data-[state=checked]:bg-volt" />
                    </Row>
                ))}
            </div>

            <div className="bg-surface border border-border rounded-sm p-6" data-testid="account-data-controls">
                <h3 className="font-heading font-bold mb-2">Account & Data</h3>
                <p className="text-xs text-muted-foreground mb-5">Download your account data or permanently delete your account and associated gameplay records.</p>
                <div className="flex flex-wrap gap-3 mb-6">
                    <button onClick={exportAccount} disabled={accountBusy}
                        className="h-10 px-4 rounded-sm border border-volt/50 text-volt hover:bg-volt/10 flex items-center gap-2 disabled:opacity-50"
                        data-testid="export-account">
                        <Download className="w-4 h-4" /> Download my data
                    </button>
                </div>
                <div className="border-t border-red-500/20 pt-5">
                    <div className="text-sm font-semibold text-red-400 mb-2">Delete account permanently</div>
                    <p className="text-xs text-muted-foreground mb-3">Cancel any active subscription first. Stored media deletion is retried automatically if the storage provider is temporarily unavailable.</p>
                    <div className="grid sm:grid-cols-2 gap-3 mb-3">
                        <input type="password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)}
                            placeholder="Current password" autoComplete="current-password"
                            className="h-10 bg-obsidian border border-border rounded-sm px-3 text-sm text-white" data-testid="delete-account-password" />
                        <input value={deleteConfirmation} onChange={(e) => setDeleteConfirmation(e.target.value)}
                            placeholder="Type DELETE"
                            className="h-10 bg-obsidian border border-border rounded-sm px-3 text-sm text-white" data-testid="delete-account-confirmation" />
                    </div>
                    <button onClick={deleteAccount} disabled={accountBusy || !deletePassword || deleteConfirmation !== "DELETE"}
                        className="h-10 px-4 rounded-sm border border-red-500/60 text-red-400 hover:bg-red-500/10 flex items-center gap-2 disabled:opacity-40"
                        data-testid="delete-account">
                        {accountBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Delete my account
                    </button>
                </div>
            </div>
        </div>
    );
}
